import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle, EnvelopeSimple, ArrowLeft, ArrowsClockwise } from "@phosphor-icons/react";

const KIND_COLORS = {
  roadmap_lead: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  corporate_inquiry: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  sponsor_interest: "bg-sky-500/15 text-sky-300 border-sky-500/40",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffHr = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHr < 1) return "just now";
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 14) return `${diffDay}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const AdminFollowUps = () => {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("uncontacted"); // uncontacted | all | by-kind
  const [marking, setMarking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/follow-ups`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const markContacted = async (row) => {
    setMarking(row.id);
    try {
      await axios.post(`${API}/admin/follow-ups/${row.kind}/${row.id}/mark-contacted`);
      toast.success(`Marked ${row.email} as contacted`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark contacted");
    } finally {
      setMarking(null);
    }
  };

  const rows = data.rows.filter((r) => {
    if (filter === "uncontacted") return !r.contacted;
    if (filter === "all") return true;
    return r.kind === filter;
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-follow-ups-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-zinc-500 hover:text-emerald-400 flex items-center gap-1 text-sm" data-testid="back-to-admin">
            <ArrowLeft size={16} /> Back to Admin
          </Link>
        </div>
        <h1 className="text-3xl font-bold mb-1">Follow-ups</h1>
        <p className="text-zinc-400 mb-8">
          Every external lead that may need a personal reply — roadmap downloads,
          corporate inquiries, and sponsorship signals — in one timeline.
        </p>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="follow-up-stats">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Uncontacted</div>
              <div className="text-3xl font-bold text-emerald-400 mt-1">{data.counts.uncontacted ?? 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Free Roadmap</div>
              <div className="text-3xl font-bold mt-1">{data.counts.roadmap_leads ?? 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Corporate</div>
              <div className="text-3xl font-bold mt-1">{data.counts.corporate_inquiries ?? 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Sponsor Interest</div>
              <div className="text-3xl font-bold mt-1">{data.counts.sponsor_interests ?? 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            ["uncontacted", "Uncontacted"],
            ["all", "All"],
            ["roadmap_lead", "Roadmap"],
            ["corporate_inquiry", "Corporate"],
            ["sponsor_interest", "Sponsor"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border transition ${
                filter === k
                  ? "bg-emerald-500 text-black border-emerald-400"
                  : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
              }`}
              data-testid={`filter-${k}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={load}
            className="ml-auto px-3 py-1.5 rounded-full text-xs font-semibold bg-zinc-900 text-zinc-300 border border-zinc-700 hover:bg-zinc-800 flex items-center gap-1"
            data-testid="refresh-follow-ups"
          >
            <ArrowsClockwise size={14} /> Refresh
          </button>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 text-center">
              <CheckCircle size={36} className="mx-auto text-emerald-400 mb-3" />
              <div className="text-lg font-semibold">Inbox zero.</div>
              <div className="text-zinc-500 text-sm mt-1">
                {filter === "uncontacted"
                  ? "Every lead has been contacted. Nice work."
                  : "No leads match this filter yet."}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="follow-up-rows">
            {rows.map((r) => (
              <Card
                key={`${r.kind}_${r.id}`}
                className={`bg-zinc-900 border-zinc-800 ${r.contacted ? "opacity-60" : ""}`}
                data-testid={`follow-up-row-${r.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${KIND_COLORS[r.kind] || ""} text-[10px] font-semibold uppercase tracking-wider`}>
                          {r.kind_label}
                        </Badge>
                        <span className="text-zinc-500 text-xs">{formatDate(r.created_at)}</span>
                        {r.contacted && (
                          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px] uppercase tracking-wider">
                            Contacted
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 font-semibold text-zinc-100 truncate">
                        {r.name || r.email}
                        {r.name && r.email && <span className="text-zinc-500 font-normal ml-2">· {r.email}</span>}
                      </div>
                      <div className="text-sm text-zinc-400 mt-1 truncate">{r.summary}</div>
                    </div>
                    <div className="flex gap-2 sm:flex-shrink-0">
                      {r.email && (
                        <a
                          href={`mailto:${r.email}`}
                          className="px-3 py-2 rounded text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1.5 border border-zinc-700"
                          data-testid={`email-btn-${r.id}`}
                        >
                          <EnvelopeSimple size={14} /> Email
                        </a>
                      )}
                      {!r.contacted && (
                        <Button
                          size="sm"
                          onClick={() => markContacted(r)}
                          disabled={marking === r.id}
                          className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs"
                          data-testid={`mark-contacted-${r.id}`}
                        >
                          {marking === r.id ? "Saving…" : "Mark contacted"}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFollowUps;
